import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { IoReload } from "react-icons/io5";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Pagination } from "../../../components/panel/table/Pagination";
import {
  FieldProps,
  TableContent,
} from "../../../components/panel/table/TableContent";
import { TableHeader } from "../../../components/panel/table/TableHeader";
import { Popover } from "../../../components/Popover";
import { TableStatus } from "../../../components/TableListOrders/TableStatus";
import {
  getOrderItems,
  getOrders as getOrdersApi,
  Order,
  OrderFilters,
  useOrderFilters,
  useOrders,
} from "../../../hook/queries/useOrdersV2";
import { useOrderPages } from "../../../hook/useOrdersPage";
import { setQueryParams } from "../../../hook/useQueryParams";
import { generateXlsx } from "../../../utils/generateXlsx";
import { OrderFilter } from "./filters-orders";

export const columns: FieldProps<Order>[] = [
  {
    name: "id",
    field: "CÓDIGO",
    viewList: true,
    tdStyles() {
      return { width: 140, whiteSpace: "nowrap" };
    },
  },
  { name: "invoiceNumber", field: "Nº NOTA FISCAL", viewList: true },
  {
    name: "client",
    field: "CLIENTE",
    viewList: true,
    render({ client }) {
      return client?.company_name ?? "-";
    },
  },
  {
    name: "seller",
    field: "REPRESENTANTE",
    viewList: true,
    render({ seller }) {
      return seller?.abbreviation;
    },
  },

  {
    name: "brand",
    field: "MARCA",
    viewList: true,
    render({ brand }) {
      return brand?.name;
    },
  },
  {
    name: "status",
    field: "SITUAÇÃO DO PEDIDO",
    viewList: true,
    tdStyles() {
      return { width: 250 };
    },
    render({ status }) {
      return (
        <div
          style={{
            fontSize: "0.765rem",
            fontWeight: "bold",
            color: status?.color,
          }}
        >
          {status?.description}
        </div>
      );
    },
  },
  {
    name: "type",
    field: "TIPO",
    viewList: true,
    tdStyles() {
      return { width: 80 };
    },
    render({ species, invoiceDate }) {
      return `${species?.cod === 9 ? "VF" : "PE"} ${moment(invoiceDate).format(
        "MM/YYYY"
      )}`;
    },
  },
  {
    name: "entryDate",
    field: "CRIADO EM",
    viewList: true,
    render({ entryDate }) {
      return moment(entryDate).format("DD/MM/YYYY");
    },
  },
  {
    name: "totalValue",
    field: "VALOR TOTAL",
    viewList: true,
    render({ totalInvoiceValue }) {
      return Number(totalInvoiceValue).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },

  { name: "cod", field: "CÓDIGO" },
  { name: "documentNumber", field: "Nº NOTA FISCAL" },
  { name: "codClient", field: "CÓD. CLIENTE" },
  { name: "cnpj", field: "CNPJ" },
  { name: "company_name", field: "RAZÃO SOCIAL" },
  { name: "codSeller", field: "CÓD. VENDEDOR" },
  { name: "rejection", field: "MOTIVO RECUSA" },
  { name: "canceled", field: "MOTIVO CANCELAMENTO" },
  { name: "status", field: "SITUAÇÃO" },
  {
    name: "orderPeriod",
    field: "PERÍODO DE CRIAÇÃO",
  },
  {
    name: "orderPresetDate",
    field: "DATA PREDEFINIDA",
  },
  {
    name: "orderSpecificDate",
    field: "DATA ESPECÍFICA",
  },
  {
    name: "orderStartDate",
    field: "DATA INICIAL",
  },
  {
    name: "orderEndDate",
    field: "DATA FINAL",
  },
  {
    name: "billingOrderPeriod",
    field: "PERÍODO DE FATURAMENTO",
  },
  {
    name: "billingOrderPresetDate",
    field: "DATA PREDEFINIDA",
  },
  {
    name: "billingOrderSpecificDate",
    field: "DATA ESPECÍFICA",
  },
  {
    name: "billingOrderStartDate",
    field: "DATA INICIAL",
  },
  {
    name: "billingOrderEndDate",
    field: "DATA FINAL",
  },
  {
    name: "statusItem",
    field: "SITUAÇÃO ITEM",
  },
  {
    name: "scores",
    field: "SCORE DO CLIENTE",
  },
];

interface ListOrdersRequest {
  admin?: boolean;
  client?: boolean;
  seller?: boolean;
  filters?: OrderFilters;
  onClearFilter?: () => void;
}

export function ListOrders({ admin, seller, client }: ListOrdersRequest) {
  const history = useHistory();
  const { addToast } = useToasts();
  const { setPages, tab, currentPage } = useOrderPages();

  const [viewFilter, setViewFilter] = useState(false);
  const [filters, setFilters] = useState<OrderFilters>(
    currentPage?.filters ?? {
      status: tab === "Faturados" ? "Faturado" : undefined,
    }
  );

  const [page, setPage] = useState<number>(currentPage?.page ?? 1);

  const formikFilter = useFormik({
    initialValues: {
      cod: undefined,
      documentNumber: undefined,
      codClient: undefined,
      cnpj: undefined,
      company_name: undefined,
      rejection: undefined,
      canceled: undefined,
      status: undefined,
      statusItem: undefined,
      scores: undefined,
      codSeller: undefined,
      brand: undefined,
      orderPeriod: undefined,
      orderPresetDate: undefined,
      orderSpecificDate: undefined,
      orderStartDate: undefined,
      orderEndDate: undefined,
      billingOrderPeriod: undefined,
      billingOrderPresetDate: undefined,
      billingOrderSpecificDate: undefined,
      billingOrderStartDate: undefined,
      billingOrderEndDate: undefined,
    },
    onSubmit: handleSubmitFilter,
  });

  function handleSubmitFilter(filter: OrderFilters) {
    setFilters(filter);
    setPage(1);
    setViewFilter(false);
  }
  async function generateReport(type: number) {
    try {
      addToast(`Exportando dados.`, {
        appearance: "info",
        autoDismiss: true,
      });

      if (type === 1) {
        const { orders } = await getOrdersApi(
          1,
          {
            ...filters,
            status: tab === "Faturados" ? "Faturado" : filters.status,
          },
          999999
        );
        if (!orders) throw new Error("Sem dados");

        if (
          admin // || userType === "sellerManager"
        ) {
          const data = orders.map((item) => ({
            "CÓD. PEDIDO": item.code,
            CLIENTE: item.client?.company_name,
            "REP.": item.seller?.cod
              ? `${item.seller?.cod} ${item.seller?.abbreviation}`
              : "-",
            MARCA: item.brand?.name,
            "STATUS DO PEDIDO": item?.status?.description,
            TIPO: item.species.cod === 9 ? "Venda Futura" : "Pronta entrega",
            "DIGITAÇÃO PEDIDO": moment(item.entryDate).format(`DD/MM/YYYY`),

            FATURAMENTO: moment(item.invoiceDate).format(
              item.species.cod === 9 ? `MM/YYYY` : `DD/MM/YYYY`
            ),

            "VALOR TOTAL": item.totalMerchandise,
          })) as any[];

          await generateXlsx<object[]>({
            filename: `Pedidos`,
            data,
          });
        } else {
          const data = orders.map((item) => ({
            "CÓD. PEDIDO": item.code,
            "Nº NOTA FISCAL": item.invoiceNumber ?? "-",
            CLIENTE: item.client?.company_name,
            "REP.": item.seller?.cod,
            MARCA: item.brand?.name,
            "STATUS DO PEDIDO": item?.status?.description,
            TIPO: item.species.cod === 9 ? "Venda Futura" : "Pronta entrega",
            "DIGITAÇÃO PEDIDO": moment(item.entryDate).format(`DD/MM/YYYY`),

            FATURAMENTO: moment(item.invoiceDate).format(
              item.species.cod === 9 ? `MM/YYYY` : `DD/MM/YYYY`
            ),

            "VALOR TOTAL": item.totalMerchandise,
          })) as any[];
          await generateXlsx<object[]>({
            filename: `Pedidos`,
            data,
          });
        }
      }

      if (type === 2) {
        const { items } = await getOrderItems(
          1,
          {
            ...filters,
            status: tab === "Faturados" ? "Faturado" : filters.status,
          },
          999999
        );
        if (!items) throw new Error("Sem dados");

        let data: any[] = [];
        for (const item of items) {
          data.push({
            "CÓD. PEDIDO": item.order.code,
            "Nº NOTA FISCAL": item.order.invoiceNumber ?? "-",
            CLIENTE: item.order.client?.company_name,
            REPRESENTANTE: item.order.seller?.abbreviation,
            MARCA: item.order.brand?.name,
            "STATUS DO PEDIDO": item.order.status.description,
            TIPO:
              item.order.species.cod === 9 ? "Venda Futura" : "Pronta entrega",
            "DIGITAÇÃO PEDIDO": moment(item.order.entryDate).format(
              `DD/MM/YYYY`
            ),
            "VALOR TOTAL PEDIDO": item.order.totalInvoiceValue,
            "FATURAMENTO PEDIDO": moment(item.order.invoiceDate).format(
              item.order.species.cod === 9 ? `MM/YYYY` : `DD/MM/YYYY`
            ),
            "CÓD. PRODUTO": item.product.cod,
            REF: item.product.reference,
            SITUAÇÃO: item.status.description,
            "MOTIVO CANCELAMENTO": item.canceled?.cod
              ? `${item.canceled?.cod} ${item.canceled?.name}`
              : "-",
            "MOTIVO RECUSA": item.rejection?.cod
              ? `${item.rejection?.cod} ${item.rejection?.name}`
              : "-",
            DESCRIÇÃO: item.product.description,
            GRADE: item.product.grid,
            QUANTIDADE: item.quantity,
            DESCONTO: item.discountValue,
            "PREÇO UNITÁRIO": Number(item.netValue) / item.quantity,
            SUBTOTAL: item.netValue,
          });
        }

        await generateXlsx<object[]>({
          filename: `Itens dos pedidos`,
          data: data,
        });
      }
    } catch (error) {
      addToast(
        "Desculpe, ocorreu um erro interno, Tente novamente mais tarde",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
    }
  }

  const getOrders = useOrders(page, {
    ...filters,
    status: tab === "Faturados" ? "Faturado" : filters.status,
  });
  const getOrderFilters = useOrderFilters({
    ...filters,
    status: tab === "Faturados" ? "Faturado" : filters.status,
  });

  useEffect(() => {
    setPages({ pageKey: tab, page, filters });
    // eslint-disable-next-line
  }, [page, filters]);
  useEffect(() => {
    if (page > 0) {
      setQueryParams({
        data: {
          field: "page",
          value: String(page),
        },
        type: "set",
        history,
      });
    }
    // eslint-disable-next-line
  }, [page, history]);
  useEffect(() => {
    formikFilter.handleReset({});
    // eslint-disable-next-line
  }, [tab]);

  return (
    <>
      <TableStatus
        data={
          getOrderFilters.data?.status.map((status) => ({
            name: status.name,
            value: String(status.id),
          })) ?? []
        }
        isLoading={getOrders.isLoading || getOrderFilters.isLoading}
        setFilters={(filters) => {
          setFilters(filters as any);
        }}
      />

      <OrderFilter
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        formikFilter={formikFilter}
        onUpdateData={() => {}}
        setViewFilter={setViewFilter}
        viewFilter={viewFilter}
        filtersData={getOrderFilters.data}
        is_admin={admin}
        is_client={client}
        is_seller={seller}
      />

      <TableHeader title="PEDIDOS">
        <Popover
          isVisible
          Button={
            <button style={{ background: "#1d6f42" }}>
              <RiFileExcel2Fill size={19} />
              <span>EXPORTAR</span>
            </button>
          }
        >
          <li
            onClick={() => {
              generateReport(1);
            }}
          >
            <span>Listagem de pedido</span>
          </li>
          <li
            onClick={() => {
              generateReport(2);
            }}
          >
            <span>Listagem de pedido com itens</span>
          </li>
        </Popover>

        <button onClick={() => getOrders.refetch()}>
          <IoReload size={19} />
          <span>RECARREGAR</span>
        </button>
      </TableHeader>
      <TableContent<Order>
        data={getOrders?.data?.orders ?? []}
        columns={columns
          .filter((f) => f.viewList)
          .filter((f) =>
            tab !== "Faturados" ? f.name !== "invoiceNumber" : true
          )}
        isLoading={getOrders.isLoading}
        onClickRow={(e) => {
          if (admin) {
            history.push(`/admin/pedidosV2/${e.id}`);
          }

          if (client) {
            history.push(`/pedidosV2/${e.id}`);
          }

          if (seller) {
            history.push(`/representante/pedidosV2/${e.id}`);
          }
        }}
      />

      <Pagination
        totalCountOfRegisters={getOrders.data?.totalCount ?? 0}
        registersPerPage={getOrders.data?.registersPerPage ?? 0}
        currentPage={page}
        onPageChange={(data) => {
          setPage(data);
        }}
      />
    </>
  );
}
